import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { UseFormRegister } from 'react-hook-form';
import { Box, Grid, InputAdornment, OutlinedInput } from "@material-ui/core";

/** Import entities */
import { CxQuestion } from "../../entity/entities";

/** Import constants */
import { QUESTION_TYPE_ATTR_FREE_TEXT } from "../../constant/Enum";
import { Pattern } from "../../constant/Pattern";

/** Import utils */
import { colorUtil } from '../../util/ColorUtil';

/** Import reducer */
import { changeFreeText, } from "./inputAnswerSlice";

/** Import hooks */
import { useAppSelector } from "../../app/hooks";
import { ErrorMessage } from "@hookform/error-message";

/** Import components */
import { QuestionText } from "./QuestionText";
import { ErrorTooltip } from '../common/ErrorToolTip';
import { useTranslation } from "react-i18next";
import DATA_QA from "../../util/DataQaAttr";

const { NO_LIMIT, ALPHANUMERIC, NUMERIC} = QUESTION_TYPE_ATTR_FREE_TEXT;

export function FreeText(props: {
  pageIndex: number,
  questionIndex: number,
  cxQuestion: CxQuestion,
  register: UseFormRegister<any>;
  errors: any;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    pageIndex,
    questionIndex,
    cxQuestion,
    cxQuestion: {questionTypeAttr},
    register,
    errors,
  } = props;

  const answerStr = useAppSelector(state => {
    const cxQuestionPage = state.inputAnswer.answer.cxAnswerPageDtos[pageIndex];
    const cxQuestion = cxQuestionPage.cxAnswers[questionIndex];

    return cxQuestion.answerStr;
  });

  // Memo
  const nameInput = useMemo(
    (): string => `freetext-${pageIndex}-${questionIndex}`,
    [pageIndex, questionIndex]
  );

  const formatedQuestionTypeAttr = useMemo(
    (): string =>
      questionTypeAttr
        ? questionTypeAttr.replace(Pattern.FilteringBracket, "")
        : '',
    [questionTypeAttr]
  );

  const numCharacters = useMemo((): number => {
    const matches = Pattern.FilteringBracket.exec(
      questionTypeAttr || '',
    );

    return (matches && matches[1]) ? parseInt(matches[1]) : 400;
  }, [questionTypeAttr])

  const isFreeTextNoLimit = useMemo((): boolean => {
    return (
      Object.values(QUESTION_TYPE_ATTR_FREE_TEXT).includes(questionTypeAttr) ||
      questionTypeAttr === null
    );
  }, [questionTypeAttr]);
  
  const validationRules = useMemo(() => {
    let rules = {};

    // Max length
    if (numCharacters > 0 && isFreeTextNoLimit) {
      rules = {
        ...rules,
        maxLength: {
          value: numCharacters,
          message: t('common.error.maximum_number_of_characters_exceeded'),
        },
      };
    }else if(numCharacters > 0) {
      rules = {
        ...rules,
        maxLength: {
          value: numCharacters,
          message: t('common.error.maximum_number_of_characters_exceeded'),
        },
        minLength: {
          value: numCharacters,
          message: t('common.error.less_than_required_character_length'),
        }
      };
    }

    // Valid text
    switch (formatedQuestionTypeAttr) {
      case ALPHANUMERIC:
        rules = {
          ...rules,
          pattern: {
            value: Pattern.SingleByteCharacterNumeric,
            message: t('common.error.contains_characters_that_cannot_be_used'),
          }
        };
        break;
      case NUMERIC:
        rules = {
          ...rules,
          pattern: {
            value: Pattern.Number,
            message: t("common.error.contains_characters_that_cannot_be_used"),
          },
        };
        break;
    }

    return rules;
  }, [numCharacters, formatedQuestionTypeAttr]);
  

  const textDescription = useMemo((): string => {
    switch (formatedQuestionTypeAttr) {
      case NUMERIC:
        return t("question.only_half_width_numeric_characters");
      case ALPHANUMERIC:
        return t("question.only_half_width_alphanumeric_characters");
      case NO_LIMIT:
        default:
          return '';
    }
  }, [formatedQuestionTypeAttr])
  const [currentNumLength, setCurrrentNumLength] = useState(answerStr?.length || 0);



  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const curVal = event.target.value;
    setCurrrentNumLength(curVal.length);
    dispatch(
      changeFreeText({
        pageIndex,
        questionIndex,
        str: curVal,
      })
    );
  }

  return (
    <>
      <QuestionText cxQuestion={cxQuestion} />
      <Box mx={4} sx={{ position: "relative" }}>
        <Grid container>
          <Grid item xs={12}>
            {textDescription}
            <OutlinedInput
              {...register(nameInput, {
                ...validationRules,
                onChange: handleChange,
              })}
              fullWidth
              multiline
              value={answerStr}
              endAdornment={
                numCharacters > 0 ? (
                  <InputAdornment
                    style={{
                      fontSize: "13px",
                      color: !!errors[nameInput]
                        ? colorUtil.errorText
                        : colorUtil.gray30,
                      position: "absolute",
                      bottom: "9.5px",
                      right: "14px",
                      verticalAlign: "bottom",
                    }}
                    position="end"
                  >
                    {currentNumLength}/{numCharacters}
                  </InputAdornment>
                ) : (
                  <></>
                )
              }
              error={!!errors[nameInput]}
              inputProps={{
                "data-qa": `${DATA_QA.INPUT.FREE_TEXT}-${
                  pageIndex + 1
                }-${questionIndex}`,
              }}
            />
            {errors && errors[nameInput] && (
              <ErrorTooltip>
                <ErrorMessage
                  name={nameInput}
                  errors={errors}
                  render={(messages) => {
                    let messageText: string = "";
                    const currentMessages = messages?.messages;
                    const errTypes = Object.keys(currentMessages || {});
                    // Priority error of pattern
                    if (errTypes.includes("pattern")) {
                      messageText = (currentMessages?.pattern as string) || "";
                    } else {
                      messageText = messages?.message || "";
                    }
                    return <>{messageText}</>;
                  }}
                />
              </ErrorTooltip>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
